import {useCallback, useEffect, useState} from 'react';

import {useSelector} from 'react-redux';

import {deleteUserCreditCard, fetchUserPaymentMethods} from '~/shared/services/bffService/api';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {UserGetPaymentMethodsResponse} from '~/shared/services/bffService/api/interfaces/responses';
import {UserCreditCard, UserPaypal} from '~/personalArea/components/Sections/Payments/types';
import {trackEvent} from '~/shared/services/analytics';
import {useLocation} from '~/shared/router';
import apiService from '~/shared/services/apiService';
import {selectUser} from '~/shared/store/storeModules/user';

const combinePaymentsInList = (paymentsData: UserGetPaymentMethodsResponse): (UserCreditCard | UserPaypal)[] => {
  const cards: UserCreditCard[] = paymentsData.creditCards || [];
  const paypals: UserPaypal[] = paymentsData.paypal.map(p => ({...p, type: 'paypal'})) || [];

  return [...cards, ...paypals];
};

const usePayments = () => {
  const [payments, setPayments] = useState<(UserCreditCard | UserPaypal)[] | null>(null);
  const [isRequestLoaded, setIsRequestLoaded] = useState(false);
  const [arePaymentsSuccessfullyFetched, setArePaymentsSuccessfullyFetched] = useState(false);
  const location = useLocation();
  const user = useSelector(selectUser);
  const getUserPayments = useCallback(async () => {
    setIsRequestLoaded(false);
    try {
      const paymentsData = await fetchUserPaymentMethods(); // Assume this function fetches user payments

      setPayments(combinePaymentsInList(paymentsData));
      setArePaymentsSuccessfullyFetched(true);
    } catch (error) {
      if (is401Error(error)) {
        handleRefreshToken(error, getUserPayments);
      }
    } finally {
      setIsRequestLoaded(true);
    }
  }, []);

  const removePaymentMethodHandler = useCallback(async (args: {
    id: number;
    type: string;
  }): Promise<boolean> => {
    try {
      const {id, type} = args;
      if (type !== 'paypal') {
        await deleteUserCreditCard(id);
      }
      if (type === 'paypal') {
        await apiService.removePayPalUserToken({paypalId: id});
      }
      trackEvent('removePaymentMethod_successful', {linkType: type, pageSource: location.pathname});
      getUserPayments();
      return true;
    } catch (error) {
      if (is401Error(error)) {
        return handleRefreshToken(error, removePaymentMethodHandler, args);
      }

      return false;
    }
  }, [getUserPayments, location.pathname]);

  useEffect(() => {
    if (!arePaymentsSuccessfullyFetched && user.loaded && !user.loading) {
      getUserPayments();
    }
  }, [getUserPayments, arePaymentsSuccessfullyFetched, user]);

  return {
    payments,
    isLoaded: isRequestLoaded && user.loaded && !user.loading,
    removePaymentMethodHandler,
    getUserPayments,
  };
};

export default usePayments;
